// What the crew is briefed on: the sealed work scope and a recorded toolbox talk.
import { KeyValue } from '@/components/ui'
import { TBT_TOPICS } from '@/domain/catalogue'
import type { RevisionView, ToolboxTalkView } from '@/domain/types'
import { textOf } from './logic'

function orDash(v: string): string {
  return v.trim() ? v : '-'
}

/** The sealed scope from the permit revision, for the giver and each attendee. */
export function PermitWorkSummary({ revision, open = true }: { revision: RevisionView | null; open?: boolean }) {
  if (!revision) return null
  const c = revision.content
  return (
    <details className="mb-4 rounded-lg border border-line bg-white px-3" open={open}>
      <summary className="cursor-pointer py-3 text-sm font-semibold text-ink">The work on this permit (revision {revision.revision_number})</summary>
      <div className="space-y-3 pb-3">
        <KeyValue
          cols={1}
          items={[
            ['Work', orDash(textOf(c.work_description))],
            ['Where the work stops (boundary)', orDash(textOf(c.work_boundary))],
            ['Location', `${revision.site_area.name}${textOf(c.location_detail) ? ` - ${textOf(c.location_detail)}` : ''}`],
            ['Tools and equipment', orDash(textOf(c.tools_equipment))],
            ['Job HSE plan', revision.jhsep ? `${revision.jhsep.reference}: ${revision.jhsep.summary}` : '-'],
          ]}
        />
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Hazards and controls</p>
          {revision.controls.length === 0 ? (
            <p className="text-sm text-ink-soft">None recorded.</p>
          ) : (
            <ul className="mt-1 space-y-1 text-sm text-ink">
              {revision.controls.map((h) => (
                <li key={h.sequence}>
                  <span className="font-semibold">{h.hazard}:</span> {h.control}
                  {h.control_kind === 'additional' ? <span className="text-ink-soft"> (additional control)</span> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </details>
  )
}

/** Topics, additional hazards, DRA plan and pause-and-check points of one talk. */
export function TalkContent({ talk }: { talk: ToolboxTalkView }) {
  const covered = new Set(talk.content.topics_covered)
  const unknown = talk.content.topics_covered.filter((code) => !TBT_TOPICS.some((t) => t.code === code))
  return (
    <div className="space-y-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Topics covered</p>
        <ul className="mt-1 grid gap-1 text-sm sm:grid-cols-2">
          {TBT_TOPICS.map((t) => (
            <li key={t.code} className="flex items-start gap-2">
              {covered.has(t.code) ? (
                <span className="font-semibold text-ok">
                  <span aria-hidden>&#10003;</span>
                  <span className="sr-only">Covered:</span>
                </span>
              ) : (
                <span className="font-semibold text-bad">
                  <span aria-hidden>&#10007;</span>
                  <span className="sr-only">Not covered:</span>
                </span>
              )}
              <span className={covered.has(t.code) ? 'text-ink' : 'text-ink-soft'}>
                {t.label}
                {covered.has(t.code) ? '' : ' (not recorded)'}
              </span>
            </li>
          ))}
          {unknown.map((code) => (
            <li key={code} className="text-ink-soft">
              Also recorded: <span className="code">{code}</span>
            </li>
          ))}
        </ul>
      </div>
      <KeyValue
        cols={1}
        items={[
          ['Additional hazards and controls', orDash(talk.content.additional_hazards)],
          ['Dynamic risk (DRA) plan', orDash(talk.content.dra_plan)],
          ['Pause and check points', orDash(talk.content.pause_and_check_points)],
        ]}
      />
    </div>
  )
}
